import { useEffect } from 'react';
import { PAGE_SEO, SITE_URL, SITE_NAME, OG_IMAGE, OG_IMAGE_ALT } from './seoConfig';

/* --------------------------------------------------------------------------
   Per-route <head> management, dependency-free.

   Every route is prerendered to static HTML at build time (scripts/prerender.js)
   with these same tags already baked in, so crawlers never depend on JavaScript.
   This hook exists for the *client-side* case: once React has hydrated, moving
   between routes has to rewrite the <head> itself.

   Copy lives in src/seoConfig.js — edit it there, not here.
   -------------------------------------------------------------------------- */

export { SITE_URL };

const meta = (attr, value) => {
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  return el;
};

const linkTag = (rel) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
};

/**
 * Applies the title, description, canonical URL and social-card tags for a route.
 *
 * @param {string} path Route path starting with "/" — must be a key of PAGE_SEO.
 */
export default function useSeo(path) {
  useEffect(() => {
    const page = PAGE_SEO[path];
    if (!page) return; // Unknown route — leave the shell defaults in place.

    const url = `${SITE_URL}${path}`;
    const { title, description } = page;

    document.title = title;
    meta('name', 'description').setAttribute('content', description);
    linkTag('canonical').setAttribute('href', url);

    meta('property', 'og:type').setAttribute('content', 'website');
    meta('property', 'og:site_name').setAttribute('content', SITE_NAME);
    meta('property', 'og:title').setAttribute('content', title);
    meta('property', 'og:description').setAttribute('content', description);
    meta('property', 'og:url').setAttribute('content', url);
    meta('property', 'og:image').setAttribute('content', OG_IMAGE);
    meta('property', 'og:image:width').setAttribute('content', '1200');
    meta('property', 'og:image:height').setAttribute('content', '630');
    meta('property', 'og:image:alt').setAttribute('content', OG_IMAGE_ALT);

    // Every indexable route may serve a full-size image preview. The shell sets
    // this too; it is repeated here because the noindex routes rewrite it and a
    // client-side navigation back out of /login must put it right again.
    meta('name', 'robots').setAttribute(
      'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );

    meta('name', 'twitter:card').setAttribute('content', 'summary_large_image');
    meta('name', 'twitter:title').setAttribute('content', title);
    meta('name', 'twitter:description').setAttribute('content', description);
    meta('name', 'twitter:image').setAttribute('content', OG_IMAGE);
    meta('name', 'twitter:image:alt').setAttribute('content', OG_IMAGE_ALT);
  }, [path]);
}
